import type { ManualCandidate } from '@/lib/api/einvoice-api'
import { cn } from '@/lib/utils'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const MAX_ROWS = 10

/** "15:53 23/09" in Vietnam time, whatever the browser's zone. */
export function formatCandidateTime(iso: string): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(iso))
    const part = (type: string) =>
        parts.find((p) => p.type === type)?.value ?? ''
    return `${part('hour')}:${part('minute')} ${part('day')}/${part('month')}`
}

export const candidateKey = (c: ManualCandidate) =>
    `${c.inv_series}|${c.inv_no}`

interface Props {
    candidates: ManualCandidate[]
    pickedKey?: string
    onPick: (candidate: ManualCandidate) => void
}

/** Short list above the form: one tap fills series, number and date. */
export function EInvoiceManualCandidates({
    candidates,
    pickedKey,
    onPick,
}: Props) {
    if (candidates.length === 0) return null
    return (
        <section className="space-y-2" aria-labelledby="manual-candidates">
            <div>
                <h3 id="manual-candidates" className="text-sm font-medium">
                    {m.einvoice_candidates_title()}
                </h3>
                <p className="text-xs text-muted-foreground">
                    {m.einvoice_candidates_hint()}
                </p>
            </div>
            <ul className="space-y-1">
                {candidates.slice(0, MAX_ROWS).map((c) => {
                    const picked = candidateKey(c) === pickedKey
                    return (
                        <li key={candidateKey(c)}>
                            <button
                                type="button"
                                aria-pressed={picked}
                                onClick={() => onPick(c)}
                                className={cn(
                                    'min-h-10 w-full rounded-md border px-3 py-2 text-left text-sm tabular-nums hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring',
                                    picked && 'border-primary bg-accent',
                                )}
                            >
                                {m.einvoice_candidate_row({
                                    number: c.inv_no,
                                    amount: formatVND(c.total_amount),
                                    time: formatCandidateTime(c.created_at),
                                })}
                            </button>
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
